// Renders an LLM-authored full HTML visual answer inside a locked-down sandboxed
// iframe (opaque origin, strict CSP, no network). The iframe is sized to its
// content height via an origin-checked postMessage, so the chat page scrolls (the
// iframe itself does not). See ./sandbox/buildHtmlSrcDoc for the security model.

import { useEffect, useMemo, useRef, useState } from 'react';
import { buildHtmlSrcDoc } from './sandbox/buildHtmlSrcDoc';
import { MIN_HEIGHT, nextVisualHeight } from './htmlVisualHeight';

const HtmlVisual: React.FC<{ html: string }> = ({ html }) => {
  const srcDoc = useMemo(() => buildHtmlSrcDoc(html), [html]);
  const [height, setHeight] = useState(MIN_HEIGHT);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const heightRef = useRef(MIN_HEIGHT);
  const growthRef = useRef(0);

  useEffect(() => {
    function onMessage(e: MessageEvent) {
      // The sandboxed iframe has an opaque origin, so its messages report origin
      // "null"; the contentWindow identity check rejects any other window/frame.
      if (e.origin !== 'null') return;
      if (!iframeRef.current || e.source !== iframeRef.current.contentWindow) return;
      const data = e.data;
      if (!data || data.__bpHtmlVisual !== true) return;
      if (typeof data.height === 'number') {
        const decision = nextVisualHeight(heightRef.current, data.height, growthRef.current);
        if (decision) {
          heightRef.current = decision.height;
          growthRef.current = decision.growth;
          setHeight(decision.height);
        }
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  if (!html) return null;

  return (
    <iframe
      ref={iframeRef}
      title="insight-visual"
      sandbox="allow-scripts"
      srcDoc={srcDoc}
      referrerPolicy="no-referrer"
      style={{ width: '100%', height, border: 0, display: 'block' }}
    />
  );
};

export default HtmlVisual;
